import * as React from "react";
import queryString from "query-string";
import { ClientStatus, IClient } from "intake_shared/src/types";
import { TSaveClient } from "../../../../clientUiTypes";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../../../../contexts/AclContext";
import { updateClientData } from "../../../../../../../store/client";
import { handleFormatDatePickerValue } from "../../../../../../../utils/utils";
import { Select } from "../../../../../bits/Select/Select";
import { ClientStatusLabels } from "../../../../../../../utils/labels";
import { TextArea } from "../../../../../bits/TextArea/TextArea";
import { DatePicker } from "../../../../../bits/DatePicker/DatePicker";
import { Dialog } from "../../../../../bits/Dialog/Dialog";
import { useChartLock } from "../../../../../../../contexts/ChartLockContext";
import { useLocation } from "react-router-dom";
import store from "../../../../../../../store";
import { hasFeeChanges } from "../../../../../../../store/feeRevision";

type Option = {
    label: React.ReactNode;
    value: string;
};

interface UpdateField {
    followup_notes: string;
    start_date: null | Date;
    end_date: null | Date;
    status_after_end_date: ClientStatus;
}

interface MoveClientToFollowUpProps {
    showMoveClientModal: boolean;
    setShowMoveClientModal: (value: boolean) => void;
    client: IClient;
    isInactiveClient: boolean;
    onSaveClient: TSaveClient;
}

const CANNOT_FILTER_BY: ClientStatus[] = [
    ClientStatus.WAITLIST,
    ClientStatus.COMPLETED,
    ClientStatus.DO_NOT_BOOK,
    ClientStatus.FOLLOWUP,
];

const getClientStatusOptions = () => {
    return Object.values(ClientStatus)
        .filter((s) => !CANNOT_FILTER_BY.includes(s))
        .map((s) => ({
            value: s,
            label: ClientStatusLabels[s] || s,
        }));
};

export default function MoveClientToFollowUp({
    client,
    isInactiveClient,
    setShowMoveClientModal,
    showMoveClientModal,
    onSaveClient,
}: MoveClientToFollowUpProps) {
    const { addToast } = useToasts();
    const { releaseChartLock } = useChartLock();
    const browserLocation = useLocation();
    const parsedQueryParams = queryString.parse(browserLocation.search || "");
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const feeChangesDirty = hasFeeChanges(client);
    const [updateClientFollowupData, setUpdateClientFollowupData] =
        React.useState<UpdateField>({
            followup_notes: "",
            start_date: null,
            end_date: null,
            status_after_end_date: "" as ClientStatus,
        });

    const handleMoveClientToFollowUp = async () => {
        try {
            const followupData = {
                start_date: `${handleFormatDatePickerValue(
                    updateClientFollowupData.start_date?.toISOString() as string
                )}T00:00:00.000`,
                followup_notes: updateClientFollowupData.followup_notes,
                end_date: `${handleFormatDatePickerValue(
                    updateClientFollowupData.end_date?.toISOString() as string
                )}T00:00:00.000`,
                ending_date: `${handleFormatDatePickerValue(
                    updateClientFollowupData.end_date?.toISOString() as string
                )}T00:00:00.000`,
                status_after_end_date:
                    updateClientFollowupData.status_after_end_date,
            };
            const updatedClient = {
                ...client,
                status: ClientStatus.FOLLOWUP,
                [ClientStatus.FOLLOWUP]: followupData,
            };

            if (isInactiveClient) {
                await store.updateArchivedClient(
                    client.id,
                    {
                        ...updatedClient,
                        last_updated_by: loggedInUserEmail as string,
                    },
                    feeChangesDirty
                );
            } else if (feeChangesDirty) {
                await store.saveClientData(
                    updatedClient,
                    loggedInUserEmail as string,
                    true,
                    true
                );
            } else {
                await updateClientData({
                    client_id: client.id,
                    changes: {
                        status: ClientStatus.FOLLOWUP,
                        [ClientStatus.FOLLOWUP]: followupData,
                    },
                    last_updated_by: loggedInUserEmail as string,
                });
            }
            onSaveClient(updatedClient);
            if (client.id && parsedQueryParams.client_type !== "new_client") {
                await releaseChartLock(client.id);
            }
            addToast("Client moved successfully", { appearance: "success" });
            setShowMoveClientModal(false);
        } catch (err) {
            addToast("There was an issue moving this client", {
                appearance: "error",
            });
        }
    };

    return (
        <Dialog
            open={showMoveClientModal}
            title="Move Client to Followup"
            onClose={() => setShowMoveClientModal(false)}
            onSave={handleMoveClientToFollowUp}
            saveText="Move Client"
            isSubmitBtnDisabled={
                !updateClientFollowupData.start_date ||
                !updateClientFollowupData.end_date
            }
        >
            <div className="v2-form">
                <div className="gridTwo">
                    <DatePicker
                        label="Start Date"
                        dateFormat={"MM/dd/yyyy"}
                        selected={updateClientFollowupData?.start_date}
                        onChange={(date) => {
                            setUpdateClientFollowupData({
                                ...updateClientFollowupData,
                                start_date: date as Date,
                            });
                        }}
                    />

                    <DatePicker
                        label="End Date"
                        dateFormat={"MM/dd/yyyy"}
                        selected={updateClientFollowupData?.end_date}
                        onChange={(date) => {
                            setUpdateClientFollowupData({
                                ...updateClientFollowupData,
                                end_date: date as Date,
                            });
                        }}
                    />
                </div>

                <Select
                    label="Choose status for client after end date"
                    onChange={(val) => {
                        setUpdateClientFollowupData({
                            ...updateClientFollowupData,
                            status_after_end_date: (val as Option)
                                .value as ClientStatus,
                        });
                    }}
                    options={[
                        { label: "Select option", value: "" },
                        ...getClientStatusOptions(),
                    ]}
                    defaultValue={getClientStatusOptions().find(
                        (status) =>
                            status.value ===
                            updateClientFollowupData.status_after_end_date
                    )}
                    isLongListInDialog
                />

                <TextArea
                    label="Follow up notes"
                    value={updateClientFollowupData?.followup_notes}
                    onChange={(e) => {
                        setUpdateClientFollowupData({
                            ...updateClientFollowupData,
                            followup_notes: e.target.value,
                        });
                    }}
                />
            </div>
        </Dialog>
    );
}
